import { useEffect, useMemo, useState } from "react";

const MenuPanel = ({
  menus = [],
  categories = [],
  isFixedMenuClient = false,
  onAddMenu,
  onEditMenu,
  onDeleteMenu,
  onToggleMenu,
  onUpdateBufferCount,
  onSaveHotSelling,
}) => {
  const [openMenuId, setOpenMenuId] = useState(null);
  const [showHotSellingModal, setShowHotSellingModal] = useState(false);
  const [hotSellingSelection, setHotSellingSelection] = useState([]);
  const [hotSellingSearch, setHotSellingSearch] = useState("");
  const [bufferInputs, setBufferInputs] = useState({});

  const [typeFilter, setTypeFilter] = useState("ALL");
  const [categoryFilter, setCategoryFilter] = useState("ALL");
  const [searchText, setSearchText] = useState("");

  const getMenuUniqueKey = (menu) =>
    isFixedMenuClient ? menu?.sortKey : menu?.menuID || menu?.sortKey;

  useEffect(() => {
    const next = {};
    menus.forEach((m) => {
      const key = getMenuUniqueKey(m);
      if (!key) return;
      next[key] = "";
    });
    setBufferInputs(next);
  }, [menus, isFixedMenuClient]);

  const getMenuName = (menu) => menu?.itemName || menu?.menuName || "-";
  const getMenuDescription = (menu) =>
    menu?.itemDescription || menu?.description || "";

  const filteredMenus = useMemo(() => {
    return menus
      .filter((m) =>
        typeFilter?.toUpperCase() === "ALL"
          ? true
          : m.itemType?.toUpperCase() === typeFilter,
      )
      .filter((m) =>
        categoryFilter?.toUpperCase() === "ALL"
          ? true
          : m.category === categoryFilter,
      )
      .filter((m) =>
        searchText
          ? getMenuName(m).toLowerCase().includes(searchText.toLowerCase())
          : true,
      );
  }, [menus, typeFilter, categoryFilter, searchText]);

  const updateBuffer = async (menu) => {
    if (!onUpdateBufferCount) return;
    const key = getMenuUniqueKey(menu);
    const value = bufferInputs[key] ?? "";

    const parsedValue = Number(value);
    if (value === "" || !/^\d+$/.test(String(value)) || parsedValue <= 0) return;

    const confirmed = window.confirm(
      `Update buffer count to ${value === "" ? 0 : parsedValue}?`
    );
    if (!confirmed) return;

    await onUpdateBufferCount(menu, parsedValue, isSoldOutFlagOn(menu));
  };

  const getStartingPrice = (splitMenu) => {
    if (!splitMenu || typeof splitMenu !== "object") return null;

    const prices = Object.values(splitMenu)
      .map(Number)
      .filter((p) => !isNaN(p) && p > 0);

    if (prices.length === 0) return null;

    return Math.min(...prices);
  };

  const getDisplayPrice = (menu) => {
    const splitPrice = getStartingPrice(menu?.splitMenu);
    if (splitPrice !== null) return splitPrice;

    const directPrice = Number(menu?.itemPrice ?? menu?.price);
    if (!Number.isNaN(directPrice) && directPrice > 0) return directPrice;

    return null;
  };

  const isMenuSoldOut = (menu) => {
    const ordered = Number(menu?.orderedCount ?? 0);
    const capacity = Number(menu?.totalCapacity ?? 0);

    if (!Number.isFinite(ordered) || !Number.isFinite(capacity)) {
      return false;
    }

    return capacity <= ordered;
  };

  const getOrderedCount = (menu) => Number(menu?.orderedCount ?? 0);
  const getTotalCapacity = (menu) => Number(menu?.totalCapacity ?? 0);

  const shouldShowBufferControls = (menu) => {
    const ordered = getOrderedCount(menu);
    const capacity = getTotalCapacity(menu);

    if (!Number.isFinite(ordered) || !Number.isFinite(capacity)) return false;

    // Show buffer controls when capacity is reached, regardless of toggle state.
    // This lets the user add buffer to turn a sold-out item back on.
    return ordered >= capacity;
  };

  const isSoldOutFlagOn = (menu) => {
    if (typeof menu?.soldOut === "string") {
      return menu.soldOut.toLowerCase() === "true";
    }

    return Boolean(menu?.soldOut);
  };
  return (
    <div className="bg-gray-100 rounded-xl p-4">
      <div className="grid grid-cols-12 gap-3 items-center mb-5">
        <div className="col-span-12 md:col-span-3">
          <select
            className="w-full bg-white border rounded-lg px-3 py-2 text-sm"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <option value="ALL">All Types</option>
            <option value="VEGETARIAN">Vegetarian</option>
            <option value="NON-VEGETARIAN">Non-Vegetarian</option>
          </select>
        </div>

        <div className="col-span-12 md:col-span-3">
          <select
            className="w-full bg-white border rounded-lg px-3 py-2 text-sm"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            <option value="ALL">All Categories</option>
            {categories?.map((cat) => (
              <option
                key={typeof cat === "string" ? cat : cat?.categoryID || cat?.categoryName}
                value={typeof cat === "string" ? cat : cat?.categoryName || ""}
              >
                {typeof cat === "string" ? cat : cat?.categoryName || ""}
              </option>
            ))}
          </select>
        </div>

        <div className="col-span-12 md:col-span-3 relative">
          <input
            type="text"
            className="w-full bg-white border rounded-lg px-3 py-2 text-sm"
            placeholder="Search by item name"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />

          {searchText && (
            <span
              onClick={() => setSearchText("")}
              className="absolute right-3 top-1/5 -translate-y-1/4 cursor-pointer text-gray-400 text-lg"
            >
              ×
            </span>
          )}
        </div>

        <div className="col-span-12 md:col-span-3 text-right flex gap-2 justify-end">
          <button
            onClick={() => {
              setHotSellingSelection(
                menus.filter((m) => m.category === "Hot Selling").map((m) => m.menuID)
              );
              setHotSellingSearch("");
              setShowHotSellingModal(true);
            }}
            className="bg-orange-500 hover:bg-orange-600 border-0 shadow-sm text-white text-sm font-medium px-4 py-2.5 rounded-xl transition-colors w-full md:w-auto"
          >
            + Hot Selling Items
          </button>
          <button
            onClick={onAddMenu}
            className="bg-green-600 hover:bg-green-700 border-0 shadow-sm text-white text-sm font-medium px-4 py-2.5 rounded-xl transition-colors w-full md:w-auto"
          >
            + Add Menu
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {filteredMenus.length === 0 && (
          <div className="col-span-full">
            <div className="bg-blue-50 text-blue-700 px-4 py-3 rounded-lg">
              No menus available.
            </div>
          </div>
        )}

        {filteredMenus.map((menu) => (
          <div
            key={getMenuUniqueKey(menu)}
            onClick={() =>
              setOpenMenuId(
                openMenuId === getMenuUniqueKey(menu)
                  ? null
                  : getMenuUniqueKey(menu)
              )
            }
            className="bg-white rounded-xl p-4 shadow-sm border hover:shadow-md transition"
          >
            <h3 className="font-semibold text-sm mb-1">{getMenuName(menu)}</h3>

            <p className="text-xs text-gray-500 mb-4 line-clamp-2">
              {getMenuDescription(menu)}
            </p>

            {getDisplayPrice(menu) !== null && (
              <p className="text-xs text-gray-500 mb-4">₹{getDisplayPrice(menu)}</p>
            )}

            <div className="flex items-center justify-between">
              {isFixedMenuClient ? (
                <div className="w-full">
                  <div className="w-full flex items-center justify-between">
                    <label className="relative inline-flex items-center cursor-pointer" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={!isSoldOutFlagOn(menu)}
                        onChange={() => {
                          // Block toggle-on when capacity is reached; require buffer count first
                          if (isSoldOutFlagOn(menu) && getOrderedCount(menu) >= getTotalCapacity(menu)) {
                            alert("Please add a buffer count before turning this item back on.");
                            return;
                          }
                          onToggleMenu(menu, !isSoldOutFlagOn(menu));
                        }}
                        className="sr-only"
                      />

                      <div
                        className={`relative w-10 h-5 rounded-full transition-colors ${
                          isSoldOutFlagOn(menu) ? "bg-gray-300" : "bg-green-500"
                        }`}
                      >
                        <span
                          className={`absolute top-[2px] w-4 h-4 bg-white rounded-full transition-all duration-300 ${
                            isSoldOutFlagOn(menu) ? "left-[2px]" : "left-[22px]"
                          }`}
                        />
                      </div>
                    </label>

                    <div className="text-right">
                      <div className="text-sm font-semibold text-gray-700">
                        {isSoldOutFlagOn(menu) ? "Sold Out" : "Live"}
                      </div>
                      <div className="text-xs text-gray-400">
                        Ordered: {getOrderedCount(menu)}
                      </div>
                      <div className="text-xs font-medium text-green-700">
                        Available: {getTotalCapacity(menu) - getOrderedCount(menu)}/{getTotalCapacity(menu)}
                      </div>
                    </div>
                  </div>

                  {shouldShowBufferControls(menu) && (
                    <div className="mt-2 flex items-center gap-2">
                      <input
                        type="number"
                        min={0}
                        className="w-24 border rounded-lg px-2 py-1 text-sm"
                        placeholder="Buffer"
                        value={bufferInputs[getMenuUniqueKey(menu)] ?? ""}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => {
                          const key = getMenuUniqueKey(menu);
                          const val = e.target.value;
                          if (val === "" || /^\d+$/.test(val)) {
                            setBufferInputs((prev) => ({ ...prev, [key]: val }));
                          }
                        }}
                      />
                      <button
                        disabled={
                          !/^\d+$/.test(String(bufferInputs[getMenuUniqueKey(menu)] ?? "")) ||
                          Number(bufferInputs[getMenuUniqueKey(menu)] ?? "") <= 0
                        }
                        className={`text-xs px-3 py-1 rounded ${
                          /^\d+$/.test(String(bufferInputs[getMenuUniqueKey(menu)] ?? "")) &&
                          Number(bufferInputs[getMenuUniqueKey(menu)] ?? "") > 0
                            ? "bg-blue-600 hover:bg-blue-700 text-white"
                            : "bg-gray-300 text-gray-500 cursor-not-allowed"
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          updateBuffer(menu);
                        }}
                      >
                        Update Buffer Count
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!menu.soldOut}
                      onChange={() => {
                        if (menu.soldOut && menu.isInStock && Number(menu.inStock) === 0) {
                          onEditMenu(menu);
                          return;
                        }
                        onToggleMenu(menu, !menu.soldOut);
                      }}
                      className="sr-only"
                    />

                    <div
                      className={`relative w-10 h-5 rounded-full transition-colors
      ${menu.soldOut ? "bg-gray-300" : "bg-green-500"}
    `}
                    >
                      <span
                        className={`
        absolute
        top-[2px]
        w-4 h-4
        bg-white
        rounded-full
        transition-all
        duration-300
        ${menu.soldOut ? "left-[2px]" : "left-[22px]"}
      `}
                      />
                    </div>
                  </label>

                  <div className="relative">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenMenuId(
                          openMenuId === getMenuUniqueKey(menu)
                            ? null
                            : getMenuUniqueKey(menu),
                        );
                      }}
                      className="border-0 font-extrabold"
                    >
                      ⋮
                    </button>

                    {openMenuId === getMenuUniqueKey(menu) && (
                      <div className="absolute right-0 mt-2 w-28 bg-white rounded-lg shadow-lg  border-0 z-20">
                        <button
                          onClick={() => {
                            onEditMenu(menu);
                            setOpenMenuId(null);
                          }}
                          className="w-full text-left px-3 py-2 text-sm border-1 border-gray-100 hover:bg-gray-100"
                        >
                          Edit
                        </button>

                        <button
                          onClick={() => {
                            onDeleteMenu(menu);
                            setOpenMenuId(null);
                          }}
                          className="w-full text-left px-3 py-2 text-sm  border-1 border-gray-100 text-red-600 hover:bg-gray-100"
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Hot Selling Items Modal */}
      {showHotSellingModal && (
        <div
          className="fixed inset-0 flex items-center justify-center z-50"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          onClick={() => setShowHotSellingModal(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <h4 className="text-lg font-bold mb-1">
                Select Hot Selling Items{" "}
                <span className="text-gray-500">({hotSellingSelection.length}/5)</span>
              </h4>
              <p className="text-sm text-gray-500 mb-4">You can select up to 5 items</p>

              <div className="relative mb-4">
                <svg
                  className="absolute left-3 top-1/3 -translate-y-1/2 text-gray-400"
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
                <input
                  type="text"
                  className="w-full border border-gray-300 rounded-xl pl-10 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                  placeholder="Search by item name"
                  value={hotSellingSearch}
                  onChange={(e) => setHotSellingSearch(e.target.value)}
                />
              </div>

              <div className="max-h-72 overflow-y-auto divide-y">
                {menus
                  .filter((m) =>
                    hotSellingSearch
                      ? m.itemName?.toLowerCase().includes(hotSellingSearch.toLowerCase())
                      : true
                  )
                  .map((menu) => {
                    const isSelected = hotSellingSelection.includes(menu.menuID);
                    const isDisabled = !isSelected && hotSellingSelection.length >= 5;

                    return (
                      <label
                        key={menu.menuID}
                        className={`flex items-center justify-between py-3 px-2 cursor-pointer ${
                          isDisabled ? "opacity-50 cursor-not-allowed" : ""
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            disabled={isDisabled}
                            onChange={() => {
                              setHotSellingSelection((prev) =>
                                isSelected
                                  ? prev.filter((id) => id !== menu.menuID)
                                  : [...prev, menu.menuID]
                              );
                            }}
                            className="w-5 h-5 accent-green-600 rounded"
                          />
                          <span className="font-medium text-sm">{menu.itemName}</span>
                        </div>
                        <span className="text-sm font-semibold text-gray-700">
                          {getStartingPrice(menu.splitMenu) !== null
                            ? `₹${getStartingPrice(menu.splitMenu)}`
                            : ""}
                        </span>
                      </label>
                    );
                  })}
              </div>
            </div>

            <div className="flex justify-center gap-3 px-6 pb-6 pt-2">
              <button
                onClick={() => setShowHotSellingModal(false)}
                className="px-6 py-2.5 rounded-xl border border-gray-300 text-sm font-medium text-gray-600 bg-gray-50 hover:bg-gray-100 transition-colors shadow-sm"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  onSaveHotSelling(hotSellingSelection);
                  setShowHotSellingModal(false);
                }}
                className="px-6 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium transition-colors shadow-sm"
              >
                Save Selection
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MenuPanel;
